/**
 * In-grid collection banner: the slider is Swiper (already loaded by the theme), so looping,
 * dragging and the click-after-drag guard come from the library. The bullets are rendered under
 * this banner's own class names, so the styling stays with the section's CSS.
 */
(function () {
  var BANNER = '.collection-grid-banner';

  /**
   * The active segment doubles as the countdown, filling from the left over the autoplay delay --
   * the same treatment the editorial cards ticker uses. --dot-progress is written onto each bullet
   * as a plain number, which the CSS feeds to scaleX(): the active one runs 0 -> 1 while the
   * seconds tick down, the rest stay at 0.
   *
   * Timed here rather than by a CSS animation so it follows the slider's own pauses instead of
   * running to its own clock: on hover and off screen Swiper pauses, and the fill has to stand
   * still with it.
   */
  function countdown(swiper, banner, interval) {
    var dots = banner.querySelector('.collection-grid-banner__dots');
    if (!dots) return;

    var elapsed = 0;
    var last = null;
    var painted = null;

    function paint(value) {
      if (painted === value) return;
      painted = value;

      Array.prototype.forEach.call(dots.children, function (dot) {
        dot.style.setProperty('--dot-progress', dot.classList.contains('is-active') ? value : 0);
      });
    }

    // Restarts the countdown whichever way the slide changed: the timer, a bullet, or a swipe.
    swiper.on('slideChange', function () {
      elapsed = 0;
      last = null;
      painted = null;
      paint(0);
    });

    function frame(now) {
      requestAnimationFrame(frame);

      var autoplay = swiper.autoplay;

      // Autoplay over for good -- the visitor took over. The segment goes back to being simply full.
      if (!autoplay || !autoplay.running) {
        last = null;
        paint(1);
        return;
      }

      // Paused (hover, off screen): the fill stands where it is until time runs again.
      if (autoplay.paused) {
        last = null;
        return;
      }

      if (last !== null) elapsed += now - last;
      last = now;

      paint(Math.min(elapsed / interval, 1));
    }

    requestAnimationFrame(frame);
  }

  function init(banner) {
    if (banner.dataset.bannerInit) return;
    if (typeof Swiper === 'undefined') return;

    var slides = banner.querySelectorAll('.collection-grid-banner__slide').length;
    if (slides < 2) return;

    banner.dataset.bannerInit = 'true';

    var interval = parseFloat(banner.dataset.autoplay || '0') * 1000;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var options = {
      loop: true,
      grabCursor: true,
      speed: 400,
      // Easier than Swiper's default half-a-slide pull: a short drag is enough to move on.
      threshold: 5,
      longSwipesRatio: 0.15,
      pagination: {
        el: banner.querySelector('.collection-grid-banner__dots'),
        clickable: true,
        bulletClass: 'collection-grid-banner__dot',
        bulletActiveClass: 'is-active'
      }
    };

    // Autoplay is a nicety, not the way to reach a slide: it gives way the moment someone takes
    // over, and never runs for visitors who ask for reduced motion.
    if (interval && !reducedMotion) {
      options.autoplay = {
        delay: interval,
        disableOnInteraction: true,
        pauseOnMouseEnter: true
      };
    }

    var swiper = new Swiper(banner, options);

    if (options.autoplay) countdown(swiper, banner, interval);

    // Nothing to play to while the banner is off screen.
    if (options.autoplay && window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!swiper.autoplay) return;
          if (entry.isIntersecting) swiper.autoplay.start();
          else swiper.autoplay.stop();
        });
      }, { threshold: 0.5 }).observe(banner);
    }
  }

  /**
   * A banner normally takes its height from the cards sharing its row. Alone in a row (placed past the last
   * product, or with the row's cards on the next line) there is nothing to give it height, so on desktop it is
   * flagged --alone and the section's CSS makes it roughly square instead of a thin strip. Which row it lands in
   * depends on the column count, which the layout switch changes live, hence measured rather than decided in
   * Liquid. Copies hidden for the other screen size (display: none) are skipped as neighbours.
   */
  var DESKTOP = window.matchMedia('(min-width: 1000px)');
  var aloneFrame = 0;

  function isShown(element) {
    return element.offsetParent !== null || getComputedStyle(element).position === 'fixed';
  }

  function sharesRow(banner, step) {
    var sibling = banner[step];
    while (sibling && !isShown(sibling)) sibling = sibling[step];
    return !!sibling && Math.abs(sibling.offsetTop - banner.offsetTop) < 2;
  }

  function updateAlone() {
    aloneFrame = 0;
    document.querySelectorAll(BANNER).forEach(function (banner) {
      var alone = DESKTOP.matches && isShown(banner)
        && !sharesRow(banner, 'previousElementSibling') && !sharesRow(banner, 'nextElementSibling');
      banner.classList.toggle('collection-grid-banner--alone', alone);
    });
  }

  function requestAloneUpdate() {
    if (!aloneFrame) aloneFrame = requestAnimationFrame(updateAlone);
  }

  // Card heights settle as images load and the layout switch changes the columns: both resize the list.
  var listObserver = window.ResizeObserver ? new ResizeObserver(requestAloneUpdate) : null;

  function observeLists() {
    if (!listObserver) return;
    document.querySelectorAll(BANNER).forEach(function (banner) {
      if (banner.parentElement && !banner.parentElement.dataset.bannerRowWatch) {
        banner.parentElement.dataset.bannerRowWatch = 'true';
        listObserver.observe(banner.parentElement);
      }
    });
  }

  window.addEventListener('resize', requestAloneUpdate);

  function initAll() {
    document.querySelectorAll(BANNER).forEach(init);
    observeLists();
    requestAloneUpdate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Filtering and paging re-render the grid with fresh banners in it. Same approach as
  // product-card-v2.js, and init() is a no-op for banners that are already wired up.
  new MutationObserver(initAll).observe(document.body, { childList: true, subtree: true });
})();
